(() => {
  if (customElements.get('sg-bundle-builder')) return;
  class BundleBuilder extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.t = JSON.parse(this.querySelector('[data-i18n]').textContent);
      this.target = 3;
      this.items = new Map();
      this.cards = [...this.querySelectorAll('[data-product]')];
      this.money = new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: this.dataset.currency || 'USD' });
      this.root = this.dataset.root || '/';
      const clubSelect = this.querySelector('[data-filter="club"]');
      [...new Set(this.cards.map(c => c.dataset.club))].sort().forEach(club => clubSelect.add(new Option(club, club)));
      const initialClub = new URLSearchParams(location.search).get('club');
      if ([...clubSelect.options].some(o => o.value === initialClub)) clubSelect.value = initialClub;
      this.addEventListener('click', event => {
        const button = event.target.closest('button');
        if (!button || button.disabled || this.busy) return;
        this.clearError();
        if (button.dataset.tier) {
          const target = Number(button.dataset.tier);
          if (this.count() > target) return this.showError(this.t.switch_error);
          this.target = target;
        } else if (button.hasAttribute('data-add')) {
          this.addCard(button.closest('[data-product]'));
        } else if (button.dataset.action) {
          const id = button.closest('[data-line]').dataset.line;
          const item = this.items.get(id);
          if (!item) return;
          if (button.dataset.action === 'increase') {
            if (this.count() >= this.target) return this.showError(this.t.full);
            if (item.quantity >= item.max) return this.showError(this.t.stock);
            item.quantity++;
          } else if (button.dataset.action === 'decrease' && item.quantity > 1) item.quantity--;
          else this.items.delete(id);
        } else if (button.hasAttribute('data-submit')) {
          this.submit();
          return;
        }
        this.render();
      });
      this.addEventListener('change', event => {
        if (event.target.matches('[data-filter]')) this.filter();
        if (event.target.matches('[data-variant]')) this.render();
      });
      this.querySelector('[data-search]').addEventListener('input', () => this.filter());
      this.filter();
      this.render();
    }
    count() { return [...this.items.values()].reduce((n, item) => n + item.quantity, 0); }
    format(cents) { return this.money.format(cents / 100); }
    showError(message) {
      const el = this.querySelector('[data-error]');
      el.textContent = message;
      el.hidden = false;
    }
    clearError() { this.querySelector('[data-error]').hidden = true; }
    addCard(card) {
      if (this.count() >= this.target) return this.showError(this.t.full);
      const select = card.querySelector('[data-variant]');
      const option = select.selectedOptions[0];
      if (option.dataset.available !== 'true') return this.showError(this.t.unavailable);
      const id = option.value;
      const existing = this.items.get(id);
      if ((existing?.quantity || 0) >= Number(option.dataset.max)) return this.showError(this.t.stock);
      if (existing) existing.quantity++;
      else this.items.set(id, {
        id, title: card.dataset.title, url: card.dataset.url,
        variant: select.options.length > 1 ? option.textContent.trim() : '',
        image: card.querySelector('img')?.currentSrc || card.querySelector('img')?.src || '',
        price: Number(option.dataset.price), max: Number(option.dataset.max), quantity: 1
      });
    }
    filter() {
      const club = this.querySelector('[data-filter="club"]').value;
      const type = this.querySelector('[data-filter="type"]').value;
      const search = this.querySelector('[data-search]').value.trim().toLowerCase();
      let visible = 0;
      this.cards.forEach(card => {
        card.hidden = Boolean((club && card.dataset.club !== club) || (type && card.dataset.type !== type) || (search && !(card.dataset.title + ' ' + card.dataset.club).toLowerCase().includes(search)));
        if (!card.hidden) visible++;
      });
      this.querySelector('[data-result-count]').textContent = this.t.count.replace('{n}', visible);
      this.querySelector('[data-no-results]').hidden = visible > 0;
    }
    render() {
      const count = this.count();
      const rate = this.target === 3 ? .25 : .20;
      const subtotal = [...this.items.values()].reduce((n, item) => n + item.price * item.quantity, 0);
      const saving = [...this.items.values()].reduce((n, item) => n + Math.round(item.price * item.quantity * rate), 0);
      this.querySelector('[data-count]').textContent = count + ' / ' + this.target;
      this.querySelector('[data-mobile-count]').textContent = count + ' / ' + this.target + ' ' + this.t.selected;
      this.querySelectorAll('[data-tier]').forEach(b => b.setAttribute('aria-pressed', String(Number(b.dataset.tier) === this.target)));
      this.querySelectorAll('.sgb-progress span').forEach((s, i) => { s.hidden = i >= this.target; s.classList.toggle('is-filled', i < count); });
      this.querySelector('[data-subtotal]').textContent = this.format(subtotal);
      this.querySelector('[data-saving]').textContent = '−' + this.format(saving);
      this.querySelector('[data-saving-label]').textContent = this.t.savings + ' (' + Math.round(rate * 100) + '%)';
      this.querySelector('[data-total]').textContent = this.format(subtotal - saving);
      this.querySelector('[data-hint]').textContent = count === this.target ? this.t.ready : (count === 2 && this.target === 3 ? this.t.upgrade : this.t.choose_more.replace('{n}', this.target - count));
      const submit = this.querySelector('[data-submit]');
      submit.disabled = this.busy || count !== this.target || this.submitted;
      submit.textContent = this.busy ? this.t.adding : this.submitted ? this.t.added : count === this.target ? this.t.cart : this.t.choose_more.replace('{n}', this.target - count);
      this.cards.forEach(card => {
        const option = card.querySelector('[data-variant]').selectedOptions[0];
        const add = card.querySelector('[data-add]');
        const available = option.dataset.available === 'true';
        add.disabled = !available || count >= this.target || this.busy || this.submitted || (this.items.get(option.value)?.quantity || 0) >= Number(option.dataset.max);
        add.textContent = available ? this.t.add + ' +' : this.t.sold_out;
        add.setAttribute('aria-label', this.t.add + ' ' + card.dataset.title);
        card.querySelector('[data-price]').textContent = this.format(Number(option.dataset.price));
      });
      const selected = this.querySelector('[data-selected]');
      selected.replaceChildren();
      if (!count) {
        const empty = document.createElement('div');
        empty.className = 'sgb-empty';
        const p = document.createElement('p'); p.textContent = this.t.empty;
        const hint = document.createElement('p'); hint.textContent = this.t.empty_hint;
        empty.append(p, hint); selected.append(empty);
      }
      for (const item of this.items.values()) {
        const line = document.createElement('div');
        line.className = 'sgb-line'; line.dataset.line = item.id;
        const image = document.createElement('img'); image.src = item.image; image.alt = ''; image.width = 55; image.height = 65;
        const content = document.createElement('div');
        const title = document.createElement('p'); title.className = 'sgb-line-title'; title.textContent = item.title.replace(/^SG /, '');
        content.append(title);
        if (item.variant) { const option = document.createElement('p'); option.className = 'sgb-line-option'; option.textContent = item.variant; content.append(option); }
        const bottom = document.createElement('div'); bottom.className = 'sgb-line-bottom';
        const controls = document.createElement('div'); controls.className = 'sgb-quantity';
        const makeButton = (action, text, label) => {
          const b = document.createElement('button'); b.type = 'button'; b.dataset.action = action; b.textContent = text; b.setAttribute('aria-label', label + ' ' + item.title); b.disabled = this.busy || this.submitted; return b;
        };
        const minus = makeButton('decrease', '−', this.t.decrease);
        const quantity = document.createElement('span'); quantity.textContent = item.quantity;
        const plus = makeButton('increase', '+', this.t.increase); plus.disabled ||= count >= this.target || item.quantity >= item.max;
        controls.append(minus, quantity, plus);
        const price = document.createElement('span'); price.textContent = this.format(item.price * item.quantity);
        bottom.append(controls, price);
        const remove = makeButton('remove', this.t.remove, this.t.remove); remove.className = 'sgb-remove';
        content.append(bottom, remove); line.append(image, content); selected.append(line);
      }
    }
    async submit() {
      if (this.busy || this.submitted || this.count() !== this.target) return;
      this.busy = true; this.clearError(); this.render();
      const group = 'club-' + (crypto.randomUUID ? crypto.randomUUID() : Date.now().toString(36));
      try {
        const response = await fetch(this.root + 'cart/add.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [...this.items.values()].map(item => ({
            id: Number(item.id), quantity: item.quantity,
            properties: { Bundle: this.t.your_bundle + ' · ' + this.target, _sg_bundle: group }
          })) })
        });
        const result = await response.json();
        if (!response.ok) {
          this.querySelector('[data-cart-link]').hidden = false;
          this.showError(result.description || result.message || this.t.error);
          return;
        }
        this.submitted = true;
        this.querySelector('[data-cart-link]').hidden = false;
        location.assign(this.root + 'cart');
      } catch (error) {
        this.submitted = true;
        this.querySelector('[data-cart-link]').hidden = false;
        this.showError(this.t.error);
      } finally {
        this.busy = false;
        this.render();
      }
    }
  }
  customElements.define('sg-bundle-builder', BundleBuilder);
})();
